/**
 * formulas.ts — Central formula engine for all calculators.
 *
 * ARCHITECTURE RULE: Zero UI logic lives here. Only pure calculation functions.
 * To audit or update formulas, this is the ONLY file you need to change.
 *
 * Sources per calculator documented inline.
 * Last verified: July 1, 2026
 */

// ═══════════════════════════════════════════════════════════
// ── CONSTANTS (update here when industry data changes) ────
// ═══════════════════════════════════════════════════════════

// CONCRETE
// Source: Sakrete.com, Quikrete.com — Verified July 2026
export const CONCRETE_BAG_YIELD: Record<number, number> = {
  40: 0.30, // cubic feet per 40 lb bag
  60: 0.45, // cubic feet per 60 lb bag
  80: 0.60, // cubic feet per 80 lb bag
};
export const CONCRETE_WASTE_FACTOR = 1.10; // +10% recommended buffer

// PAINT
// Source: Sherwin-Williams, Lowes.com — Verified July 2026
export const PAINT_COVERAGE: Record<string, number> = {
  normal: 400,      // sq ft per gallon — normal interior surface
  porous: 300,      // sq ft per gallon — textured/porous/masonry
  dark_to_light: 300, // sq ft per gallon — heavy color change
};
export const PAINT_DOOR_AREA_SQFT = 21;    // industry standard deduction per door
export const PAINT_WINDOW_AREA_SQFT = 15;  // industry standard deduction per window
export const PAINT_WASTE_FACTOR = 1.10;

// FLOORING
// Source: Flooring America, inchcalculator.com — Verified July 2026
export const FLOORING_WASTE: Record<string, number> = {
  straight:    1.05,
  staggered:   1.10,
  diagonal:    1.15,
  herringbone: 1.20,
};

// MULCH / GRAVEL
// Source: centralhomesupply.com, calculatorsoup.com — Verified July 2026
export const MULCH_WASTE_FACTOR = 1.10; // +10% for settling

// DRYWALL
// Source: USG, Georgia-Pacific, inchcalculator.com — Verified July 2026
export const DRYWALL_SHEET_AREA: Record<string, number> = {
  "4x8":  32, // sq ft per sheet
  "4x12": 48, // sq ft per sheet
};
export const DRYWALL_WASTE_FACTOR = 1.10;
// Accessories per 1,000 sq ft of drywall (reference only)
export const DRYWALL_SCREWS_PER_SHEET = 32;
export const DRYWALL_TAPE_PER_1000SQFT = 370; // linear feet of joint tape

// FENCE
// Source: Lowes.com, OmniCalculator.com — Verified July 2026
export const FENCE_RAILS_PER_HEIGHT: Record<number, number> = {
  4: 2,
  6: 3,
  8: 3,
};
export const FENCE_CONCRETE_BAGS_PER_POST = 1; // 80lb bag per post
export const FENCE_WASTE_FACTOR = 1.10;

// TILE
// Source: LATICRETE, inchcalculator.com — Verified July 2026
export const TILE_WASTE: Record<string, number> = {
  straight:    1.10,
  diagonal:    1.15,
  offset:      1.10,
  herringbone: 1.20,
};
export const TILE_GROUT_COVERAGE_SQFT = 50; // sq ft per bag — varies by tile & joint size

// ═══════════════════════════════════════════════════════════
// ── UTILITY HELPERS ───────────────────────────────────────
// ═══════════════════════════════════════════════════════════

export function ftToM(ft: number): number  { return ft * 0.3048; }
export function mToFt(m: number): number   { return m / 0.3048; }
export function sqFtToSqM(sqft: number): number { return sqft * 0.0929; }
export function sqMToSqFt(sqm: number): number  { return sqm * 10.7639; }
export function cuFtToCuYd(cuft: number): number { return cuft / 27; }
export function cuYdToCuFt(cuyd: number): number { return cuyd * 27; }

/** Round UP to nearest integer */
export function ceilInt(n: number): number { return Math.ceil(n); }

/** Round to fixed decimal places */
export function round(n: number, decimals = 2): number {
  return Math.round(n * 10 ** decimals) / 10 ** decimals;
}

/** Convert feet + inches to decimal feet */
export function feetInchesToFt(feet: number, inches: number): number {
  return feet + inches / 12;
}


// ═══════════════════════════════════════════════════════════
// ── 1. CONCRETE CALCULATOR ───────────────────────────────
// Source: Sakrete, Quikrete, Cemex USA — Verified July 2026
// ═══════════════════════════════════════════════════════════

export interface ConcreteInput {
  shape: "slab" | "column" | "footing";
  // slab / footing
  lengthFt?: number;
  widthFt?: number;
  thicknessIn?: number;
  // column
  diameterIn?: number;
  heightFt?: number;
  bagSizeLb: 40 | 60 | 80;
}

export interface ConcreteResult {
  cubicFeet: number;
  cubicYards: number;
  bagsNeeded: number;
  bagsWithWaste: number;
  // also show all 3 bag sizes
  allBagSizes: { size: number; bags: number; bagsWithWaste: number }[];
}

export function calcConcrete(input: ConcreteInput): ConcreteResult {
  let cubicFt = 0;

  if (input.shape === "column") {
    const radiusFt = ((input.diameterIn ?? 12) / 2) / 12;
    const heightFt = input.heightFt ?? 1;
    cubicFt = Math.PI * radiusFt ** 2 * heightFt;
  } else {
    // slab or footing
    const L = input.lengthFt ?? 0;
    const W = input.widthFt ?? 0;
    const T = (input.thicknessIn ?? 4) / 12;
    cubicFt = L * W * T;
  }

  const cubicYards = cuFtToCuYd(cubicFt);
  const yield_ = CONCRETE_BAG_YIELD[input.bagSizeLb];
  const bagsNeeded = ceilInt(cubicFt / yield_);
  const bagsWithWaste = ceilInt(bagsNeeded * CONCRETE_WASTE_FACTOR);

  const allBagSizes = ([40, 60, 80] as const).map((size) => {
    const y = CONCRETE_BAG_YIELD[size];
    const b = ceilInt(cubicFt / y);
    return { size, bags: b, bagsWithWaste: ceilInt(b * CONCRETE_WASTE_FACTOR) };
  });

  return {
    cubicFeet: round(cubicFt),
    cubicYards: round(cubicYards, 3),
    bagsNeeded,
    bagsWithWaste,
    allBagSizes,
  };
}


// ═══════════════════════════════════════════════════════════
// ── 2. PAINT CALCULATOR ──────────────────────────────────
// Source: Sherwin-Williams, Lowes, facadecolorizer.com — July 2026
// ═══════════════════════════════════════════════════════════

export interface PaintInput {
  lengthFt: number;
  widthFt: number;
  heightFt: number;
  numDoors: number;
  numWindows: number;
  numCoats: number;
  surfaceType: "normal" | "porous" | "dark_to_light";
  includeCeiling: boolean;
}

export interface PaintResult {
  grossWallArea: number;
  netPaintableArea: number;
  ceilingArea: number;
  totalArea: number;
  gallonsExact: number;
  gallonsRecommended: number;
  quartsRecommended: number;
  coveragePerGallon: number;
}

export function calcPaint(input: PaintInput): PaintResult {
  const perimeter = 2 * (input.lengthFt + input.widthFt);
  const grossWall = perimeter * input.heightFt;
  const deductions =
    input.numDoors * PAINT_DOOR_AREA_SQFT +
    input.numWindows * PAINT_WINDOW_AREA_SQFT;
  const netWall = Math.max(0, grossWall - deductions);
  const ceilingArea = input.includeCeiling ? input.lengthFt * input.widthFt : 0;
  const totalArea = netWall + ceilingArea;

  const coverage = PAINT_COVERAGE[input.surfaceType];
  const gallonsExact = (totalArea / coverage) * input.numCoats;
  const gallonsRecommended = Math.ceil(gallonsExact * PAINT_WASTE_FACTOR);
  // Convert to quarts if < 1 gallon
  const quartsRecommended = Math.ceil(gallonsExact * PAINT_WASTE_FACTOR * 4);

  return {
    grossWallArea: round(grossWall),
    netPaintableArea: round(netWall),
    ceilingArea: round(ceilingArea),
    totalArea: round(totalArea),
    gallonsExact: round(gallonsExact, 2),
    gallonsRecommended,
    quartsRecommended,
    coveragePerGallon: coverage,
  };
}


// ═══════════════════════════════════════════════════════════
// ── 3. FLOORING CALCULATOR ───────────────────────────────
// Source: Flooring America, inchcalculator.com — July 2026
// ═══════════════════════════════════════════════════════════

export interface FlooringInput {
  lengthFt: number;
  widthFt: number;
  installationType: "straight" | "staggered" | "diagonal" | "herringbone";
  coveragePerBox?: number; // sq ft per box (optional)
}

export interface FlooringResult {
  netAreaSqFt: number;
  netAreaSqM: number;
  totalAreaSqFt: number;
  totalAreaSqM: number;
  totalAreaSqYd: number;
  wasteFactor: number;
  wastePercent: number;
  boxesNeeded?: number;
}

export function calcFlooring(input: FlooringInput): FlooringResult {
  const netArea = input.lengthFt * input.widthFt;
  const factor = FLOORING_WASTE[input.installationType];
  const totalArea = netArea * factor;

  return {
    netAreaSqFt: round(netArea),
    netAreaSqM: round(sqFtToSqM(netArea)),
    totalAreaSqFt: round(totalArea),
    totalAreaSqM: round(sqFtToSqM(totalArea)),
    totalAreaSqYd: round(totalArea / 9),
    wasteFactor: factor,
    wastePercent: Math.round((factor - 1) * 100),
    boxesNeeded: input.coveragePerBox
      ? ceilInt(totalArea / input.coveragePerBox)
      : undefined,
  };
}


// ═══════════════════════════════════════════════════════════
// ── 4. AREA CALCULATOR ───────────────────────────────────
// Source: inchcalculator.com, metric-conversions.org — July 2026
// ═══════════════════════════════════════════════════════════

export type AreaShape = "rectangle" | "triangle" | "circle" | "trapezoid";
export type AreaUnit = "ft" | "m" | "in" | "cm" | "yd";

export interface AreaInput {
  shape: AreaShape;
  unit: AreaUnit;
  // rectangle
  length?: number;
  width?: number;
  // triangle
  base?: number;
  triangleHeight?: number;
  // circle
  radius?: number;
  diameter?: number;
  // trapezoid
  base1?: number;
  base2?: number;
  trapezoidHeight?: number;
}

export interface AreaResult {
  areaNative: number;   // in the user's chosen unit²
  areaSqFt: number;
  areaSqM: number;
  areaSqYd: number;
  unit: AreaUnit;
}

function toFt(value: number, unit: AreaUnit): number {
  switch (unit) {
    case "ft": return value;
    case "m":  return value * 3.28084;
    case "in": return value / 12;
    case "cm": return value / 30.48;
    case "yd": return value * 3;
  }
}

export function calcArea(input: AreaInput): AreaResult {
  let areaFt = 0;
  const u = input.unit;

  switch (input.shape) {
    case "rectangle": {
      const L = toFt(input.length ?? 0, u);
      const W = toFt(input.width ?? 0, u);
      areaFt = L * W;
      break;
    }
    case "triangle": {
      const B = toFt(input.base ?? 0, u);
      const H = toFt(input.triangleHeight ?? 0, u);
      areaFt = (B * H) / 2;
      break;
    }
    case "circle": {
      const diam = input.diameter ?? (input.radius ? input.radius * 2 : 0);
      const R = toFt(diam / 2, u);
      areaFt = Math.PI * R ** 2;
      break;
    }
    case "trapezoid": {
      const B1 = toFt(input.base1 ?? 0, u);
      const B2 = toFt(input.base2 ?? 0, u);
      const H  = toFt(input.trapezoidHeight ?? 0, u);
      areaFt = ((B1 + B2) / 2) * H;
      break;
    }
  }

  // Convert back to native unit for display
  let areaNative = areaFt;
  switch (u) {
    case "m":  areaNative = sqFtToSqM(areaFt); break;
    case "in": areaNative = areaFt * 144; break;
    case "cm": areaNative = areaFt * 929.03; break;
    case "yd": areaNative = areaFt / 9; break;
    default:   areaNative = areaFt; // ft²
  }

  return {
    areaNative: round(areaNative, 3),
    areaSqFt: round(areaFt, 3),
    areaSqM: round(sqFtToSqM(areaFt), 3),
    areaSqYd: round(areaFt / 9, 3),
    unit: u,
  };
}


// ═══════════════════════════════════════════════════════════
// ── 5. FENCE CALCULATOR ──────────────────────────────────
// Source: Lowes.com, OmniCalculator, materialcalc.com — July 2026
// ═══════════════════════════════════════════════════════════

export interface FenceInput {
  totalLengthFt: number;
  heightFt: 4 | 6 | 8;
  postSpacingFt: 6 | 8;
  picketWidthIn: number;
  gapBetweenPicketsIn: number;
  numGates: number;
}

export interface FenceResult {
  numPosts: number;
  numSections: number;
  numRails: number;
  picketsBase: number;
  picketsWithWaste: number;
  concreteBags: number;
}

export function calcFence(input: FenceInput): FenceResult {
  const numPosts = ceilInt(input.totalLengthFt / input.postSpacingFt) + 1 + input.numGates * 2;
  const numSections = numPosts - 1;
  const railsPerSection = FENCE_RAILS_PER_HEIGHT[input.heightFt] ?? 3;
  const numRails = numSections * railsPerSection;

  const totalLengthIn = input.totalLengthFt * 12;
  const picketsBase = ceilInt(totalLengthIn / (input.picketWidthIn + input.gapBetweenPicketsIn));
  const picketsWithWaste = ceilInt(picketsBase * FENCE_WASTE_FACTOR);
  const concreteBags = numPosts * FENCE_CONCRETE_BAGS_PER_POST;

  return { numPosts, numSections, numRails, picketsBase, picketsWithWaste, concreteBags };
}


// ═══════════════════════════════════════════════════════════
// ── 6. MULCH / GRAVEL CALCULATOR ─────────────────────────
// Source: centralhomesupply.com, calculatorsoup.com — July 2026
// ═══════════════════════════════════════════════════════════

export interface MulchInput {
  shape: "rectangular" | "circular";
  lengthFt?: number;
  widthFt?: number;
  diameterFt?: number;
  depthIn: number;
  bagVolumeCuFt?: number; // volume per bag (e.g. 2 cu ft)
}

export interface MulchResult {
  areaSqFt: number;
  cubicFt: number;
  cubicYards: number;
  cubicYardsWithBuffer: number;
  bagsNeeded?: number;
  bagsWithBuffer?: number;
}

export function calcMulch(input: MulchInput): MulchResult {
  let areaSqFt = 0;
  if (input.shape === "rectangular") {
    areaSqFt = (input.lengthFt ?? 0) * (input.widthFt ?? 0);
  } else {
    const r = (input.diameterFt ?? 0) / 2;
    areaSqFt = Math.PI * r ** 2;
  }

  const depthFt = input.depthIn / 12;
  const cubicFt = areaSqFt * depthFt;
  const cubicYards = cuFtToCuYd(cubicFt);
  const cubicYardsWithBuffer = cubicYards * MULCH_WASTE_FACTOR;

  let bagsNeeded: number | undefined;
  let bagsWithBuffer: number | undefined;
  if (input.bagVolumeCuFt) {
    bagsNeeded = ceilInt(cubicFt / input.bagVolumeCuFt);
    bagsWithBuffer = ceilInt(bagsNeeded * MULCH_WASTE_FACTOR);
  }

  return {
    areaSqFt: round(areaSqFt),
    cubicFt: round(cubicFt, 2),
    cubicYards: round(cubicYards, 3),
    cubicYardsWithBuffer: round(cubicYardsWithBuffer, 3),
    bagsNeeded,
    bagsWithBuffer,
  };
}


// ═══════════════════════════════════════════════════════════
// ── 7. DRYWALL CALCULATOR ────────────────────────────────
// Source: USG, Georgia-Pacific, HomeDepot — July 2026
// ═══════════════════════════════════════════════════════════

export interface DrywallInput {
  lengthFt: number;
  widthFt: number;
  heightFt: number;
  includeCeiling: boolean;
  sheetSize: "4x8" | "4x12";
}

export interface DrywallResult {
  wallAreaSqFt: number;
  ceilingAreaSqFt: number;
  totalAreaSqFt: number;
  sheetsNeeded: number;
  sheetsWithWaste: number;
  screwsEstimate: number;
  tapeFt: number;
}

export function calcDrywall(input: DrywallInput): DrywallResult {
  const wallArea = 2 * (input.lengthFt + input.widthFt) * input.heightFt;
  const ceilingArea = input.includeCeiling ? input.lengthFt * input.widthFt : 0;
  const totalArea = wallArea + ceilingArea;

  const sheetArea = DRYWALL_SHEET_AREA[input.sheetSize];
  const sheetsNeeded = ceilInt(totalArea / sheetArea);
  const sheetsWithWaste = ceilInt(sheetsNeeded * DRYWALL_WASTE_FACTOR);

  const screwsEstimate = sheetsNeeded * DRYWALL_SCREWS_PER_SHEET;
  const tapeFt = Math.ceil((totalArea / 1000) * DRYWALL_TAPE_PER_1000SQFT);

  return {
    wallAreaSqFt: round(wallArea),
    ceilingAreaSqFt: round(ceilingArea),
    totalAreaSqFt: round(totalArea),
    sheetsNeeded,
    sheetsWithWaste,
    screwsEstimate,
    tapeFt,
  };
}


// ═══════════════════════════════════════════════════════════
// ── 8. TILE CALCULATOR ───────────────────────────────────
// Source: LATICRETE, inchcalculator.com — July 2026
// ═══════════════════════════════════════════════════════════

export interface TileInput {
  lengthFt: number;
  widthFt: number;
  tileWidthIn: number;
  tileHeightIn: number;
  pattern: "straight" | "diagonal" | "offset" | "herringbone";
  groutJointIn: number;
  tilesPerBox?: number;
}

export interface TileResult {
  areaSqFt: number;
  areaSqM: number;
  tilesBase: number;
  tilesTotal: number;
  boxesNeeded?: number;
  groutBags: number;
  wasteFactor: number;
}

export function calcTile(input: TileInput): TileResult {
  const areaSqFt = input.lengthFt * input.widthFt;
  const tileAreaSqFt = (input.tileWidthIn / 12) * (input.tileHeightIn / 12);
  const factor = TILE_WASTE[input.pattern];
  const tilesBase = ceilInt(areaSqFt / tileAreaSqFt);
  const tilesTotal = ceilInt(tilesBase * factor);
  const boxesNeeded = input.tilesPerBox
    ? ceilInt(tilesTotal / input.tilesPerBox)
    : undefined;
  const groutBags = ceilInt(areaSqFt / TILE_GROUT_COVERAGE_SQFT);

  return {
    areaSqFt: round(areaSqFt),
    areaSqM: round(sqFtToSqM(areaSqFt)),
    tilesBase,
    tilesTotal,
    boxesNeeded,
    groutBags,
    wasteFactor: factor,
  };
}


// ═══════════════════════════════════════════════════════════════════════
// ══════════════  GROUP B — HIGH-VALUE COST ESTIMATORS  ═════════════════
// ═══════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────────────
// 9. SOLAR SAVINGS CALCULATOR
// Sources: EIA.gov, SEIA.org, NREL.gov, EnergySage.com
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

// ⚠️  UPDATE THESE VALUES periodically from EIA.gov / SEIA.org
// Average residential electricity rate — EIA July 2026
export const SOLAR_AVG_ELECTRICITY_RATE_USD_KWH = 0.16;
// Average installed cost per watt — SEIA Q1 2026
export const SOLAR_AVG_COST_PER_WATT_INSTALLED  = 2.80;
// Federal Solar Investment Tax Credit — IRA, effective 2026
export const SOLAR_FEDERAL_ITC_PCT              = 0.30;
// Typical system efficiency factor (inverter + wiring + temp losses)
export const SOLAR_SYSTEM_EFFICIENCY            = 0.80;
// Annual production factor kWh/kW (US average — NREL)
export const SOLAR_ANNUAL_PRODUCTION_FACTOR_KWH = 1300;
// Panel standard lifespan (years)
export const SOLAR_PANEL_LIFESPAN_YEARS         = 25;

export interface SolarInput {
  monthlyKwh: number;
  peakSunHours: number;
  panelWatts: number;
  electricityRate: number;
  costPerWatt: number;
  coveragePct: number;
  applyITC: boolean;
}

export interface SolarResult {
  systemKw: number;
  numPanels: number;
  annualProductionKwh: number;
  monthlySavings: number;
  annualSavings: number;
  systemGrossCost: number;
  taxCredit: number;
  systemNetCost: number;
  paybackYears: number;
  savings25Years: number;
  billCoverageActualPct: number;
}

export function calcSolar(input: SolarInput): SolarResult {
  const dailyKwh   = input.monthlyKwh / 30;
  const dcKwNeeded = (dailyKwh / input.peakSunHours) / SOLAR_SYSTEM_EFFICIENCY;
  const dcKwTarget = dcKwNeeded * (input.coveragePct / 100);

  const numPanels  = Math.ceil((dcKwTarget * 1000) / input.panelWatts);
  const systemKw   = round((numPanels * input.panelWatts) / 1000, 2);

  const annualProductionKwh = round(systemKw * SOLAR_ANNUAL_PRODUCTION_FACTOR_KWH);
  const annualSavings       = round(annualProductionKwh * input.electricityRate, 2);
  const monthlySavings      = round(annualSavings / 12, 2);

  const systemGrossCost = round(systemKw * 1000 * input.costPerWatt);
  const taxCredit       = input.applyITC ? round(systemGrossCost * SOLAR_FEDERAL_ITC_PCT) : 0;
  const systemNetCost   = systemGrossCost - taxCredit;

  const paybackYears    = annualSavings > 0 ? round(systemNetCost / annualSavings, 1) : 0;
  const savings25Years  = round((annualSavings * SOLAR_PANEL_LIFESPAN_YEARS) - systemNetCost);
  const billCoverageActualPct = round(
    Math.min(100, (annualProductionKwh / (input.monthlyKwh * 12)) * 100), 1
  );

  return {
    systemKw, numPanels, annualProductionKwh,
    monthlySavings, annualSavings,
    systemGrossCost, taxCredit, systemNetCost,
    paybackYears, savings25Years, billCoverageActualPct,
  };
}


// ─────────────────────────────────────────────────────────────────────
// 10. HVAC REPLACEMENT COST CALCULATOR
// Sources: Modernize.com, HomeGuide.com, This Old House 2026, Angi.com
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const HVAC_COST_PER_TON: Record<string, number> = {
  central:   1800,
  ac_only:   1500,
  heat_pump: 2000,
  minisplit: 3000,
};
export const HVAC_DUCT_COST: Record<string, number> = {
  small: 2100, medium: 3200, large: 4300,
};
export const HVAC_EFFICIENCY_MULTIPLIER: Record<string, number> = {
  standard: 1.00, high: 1.20, premium: 1.45,
};
export const HVAC_CLIMATE_MULTIPLIER: Record<string, number> = {
  cold: 0.90, moderate: 1.00, hot_humid: 1.10, hot_dry: 1.05,
};
export const HVAC_VARIANCE     = 0.20;
export const HVAC_SQFT_PER_TON = 500;

export type HvacSystemType = "central" | "ac_only" | "heat_pump" | "minisplit";
export type HvacEfficiency  = "standard" | "high" | "premium";
export type HvacClimate     = "cold" | "moderate" | "hot_humid" | "hot_dry";

export interface HvacInput {
  sqft: number;
  systemType: HvacSystemType;
  efficiency: HvacEfficiency;
  climate: HvacClimate;
  replaceDucts: boolean;
}

export interface HvacResult {
  estimatedTons: number;
  costMin: number;
  costMax: number;
  costMidpoint: number;
  ductCost: number;
  totalMin: number;
  totalMax: number;
}

function roundToHalfTon(t: number): number { return Math.ceil(t * 2) / 2; }

export function calcHvac(input: HvacInput): HvacResult {
  const adjTons       = (input.sqft / HVAC_SQFT_PER_TON) * HVAC_CLIMATE_MULTIPLIER[input.climate];
  const estimatedTons = roundToHalfTon(adjTons);
  const baseCost      = estimatedTons * HVAC_COST_PER_TON[input.systemType] * HVAC_EFFICIENCY_MULTIPLIER[input.efficiency];

  const sizeKey  = input.sqft < 1500 ? "small" : input.sqft <= 2500 ? "medium" : "large";
  const ductCost = input.replaceDucts ? HVAC_DUCT_COST[sizeKey] : 0;

  return {
    estimatedTons,
    costMidpoint: round(baseCost),
    costMin: round(baseCost * (1 - HVAC_VARIANCE)),
    costMax: round(baseCost * (1 + HVAC_VARIANCE)),
    ductCost,
    totalMin: round(baseCost * (1 - HVAC_VARIANCE) + ductCost),
    totalMax: round(baseCost * (1 + HVAC_VARIANCE) + ductCost),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 11. ROOFING COST CALCULATOR
// Sources: This Old House 2026, HomeGuide.com, Modernize.com
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const ROOFING_COST_PER_SQUARE: Record<string, { min: number; avg: number; max: number }> = {
  asphalt: { min: 500,  avg: 650,  max: 800  },
  wood:    { min: 700,  avg: 950,  max: 1200 },
  metal:   { min: 900,  avg: 1250, max: 1600 },
  premium: { min: 2300, avg: 2800, max: 3500 },
};
export const ROOFING_PITCH_FACTOR: Record<string, number> = {
  low: 1.07, medium: 1.19, steep: 1.36,
};
export const ROOFING_OVERLAY_DISCOUNT = 0.75;
export const ROOFING_EXTRA_COST       = 350;
export const ROOFING_VARIANCE         = 0.20;

export type RoofingMaterial = "asphalt" | "wood" | "metal" | "premium";
export type RoofingPitch    = "low" | "medium" | "steep";
export type RoofingWork     = "tearoff" | "overlay";

export interface RoofingInput {
  footprintSqFt: number;
  pitch: RoofingPitch;
  material: RoofingMaterial;
  workType: RoofingWork;
  numExtras: number;
}

export interface RoofingResult {
  roofSqFt: number;
  roofSquares: number;
  costPerSquare: number;
  baseCost: number;
  extrasCost: number;
  totalMin: number;
  totalMax: number;
  totalMid: number;
}

export function calcRoofing(input: RoofingInput): RoofingResult {
  const roofSqFt    = round(input.footprintSqFt * ROOFING_PITCH_FACTOR[input.pitch]);
  const roofSquares = round(roofSqFt / 100, 1);
  const costs       = ROOFING_COST_PER_SQUARE[input.material];
  const workMul     = input.workType === "overlay" ? ROOFING_OVERLAY_DISCOUNT : 1;
  const costPerSquare = round(costs.avg * workMul);
  const baseCost    = round(roofSquares * costPerSquare);
  const extrasCost  = input.numExtras * ROOFING_EXTRA_COST;
  const total       = baseCost + extrasCost;

  return {
    roofSqFt, roofSquares, costPerSquare, baseCost, extrasCost,
    totalMin: round(total * (1 - ROOFING_VARIANCE)),
    totalMax: round(total * (1 + ROOFING_VARIANCE)),
    totalMid: total,
  };
}


// ─────────────────────────────────────────────────────────────────────
// 12. KITCHEN REMODEL COST CALCULATOR
// Sources: NKBA, Angi.com 2026, myhomeus.com
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const KITCHEN_COST_PER_SQFT: Record<string, { min: number; avg: number; max: number }> = {
  cosmetic: { min: 75,  avg: 112, max: 150 },
  midrange: { min: 150, avg: 225, max: 300 },
  highend:  { min: 300, avg: 400, max: 500 },
};
export const KITCHEN_LAYOUT_BASE     = 3000;
export const KITCHEN_LAYOUT_PER_SQFT = 50;
export const KITCHEN_VARIANCE        = 0.30;
export const KITCHEN_NKBA_PCT        = 0.15;

export type KitchenTier = "cosmetic" | "midrange" | "highend";

export interface KitchenInput {
  sqft: number;
  tier: KitchenTier;
  layoutChange: boolean;
  homeValue?: number;
}

export interface KitchenResult {
  costPerSqFt: number;
  layoutChangeCost: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
  nkbaReasonable?: boolean;
  nkbaMaxBudget?: number;
}

export function calcKitchen(input: KitchenInput): KitchenResult {
  const costs = KITCHEN_COST_PER_SQFT[input.tier];
  const baseMid = input.sqft * costs.avg;
  const layoutChangeCost = input.layoutChange
    ? KITCHEN_LAYOUT_BASE + input.sqft * KITCHEN_LAYOUT_PER_SQFT
    : 0;
  const totalMid = baseMid + layoutChangeCost;

  let nkbaReasonable: boolean | undefined;
  let nkbaMaxBudget: number | undefined;
  if (input.homeValue && input.homeValue > 0) {
    nkbaMaxBudget  = round(input.homeValue * KITCHEN_NKBA_PCT);
    nkbaReasonable = totalMid <= nkbaMaxBudget;
  }

  return {
    costPerSqFt: costs.avg, layoutChangeCost,
    totalMin: round(totalMid * (1 - KITCHEN_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + KITCHEN_VARIANCE)),
    nkbaReasonable, nkbaMaxBudget,
  };
}


// ─────────────────────────────────────────────────────────────────────
// 13. BATHROOM REMODEL COST CALCULATOR
// Sources: Angi.com, NKBA, USACabinetStore.com — July 2026
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const BATH_COST_PER_SQFT: Record<string, { avg: number }> = {
  basic:    { avg: 115 },
  midrange: { avg: 265 },
  luxury:   { avg: 575 },
};
export const BATH_MINIMUMS: Record<string, number> = {
  half: 3000, full: 8000, master: 15000,
};
export const BATH_PLUMBING_MOVE_COST = 3500;
export const BATH_SHOWER_COST: Record<string, number> = {
  existing: 0, shower: 3000, tub: 2000, both: 4500,
};
export const BATH_VARIANCE      = 0.20;
export const BATH_NAR_JOY_SCORE = 9.8;
export const BATH_NAR_ROI_PCT   = 50;

export type BathType   = "half" | "full" | "master";
export type BathTier   = "basic" | "midrange" | "luxury";
export type BathShower = "existing" | "shower" | "tub" | "both";

export interface BathroomInput {
  sqft: number;
  bathType: BathType;
  tier: BathTier;
  movePlumbing: boolean;
  showerTub: BathShower;
}

export interface BathroomResult {
  costPerSqFt: number;
  baseEstimate: number;
  plumbingCost: number;
  showerTubCost: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
  joyScore: number;
  roiPct: number;
}

export function calcBathroom(input: BathroomInput): BathroomResult {
  const avg          = BATH_COST_PER_SQFT[input.tier].avg;
  const baseEstimate = round(Math.max(input.sqft * avg, BATH_MINIMUMS[input.bathType]));
  const plumbingCost = input.movePlumbing ? BATH_PLUMBING_MOVE_COST : 0;
  const showerTubCost = BATH_SHOWER_COST[input.showerTub];
  const totalMid     = baseEstimate + plumbingCost + showerTubCost;

  return {
    costPerSqFt: avg, baseEstimate, plumbingCost, showerTubCost,
    totalMin: round(totalMid * (1 - BATH_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + BATH_VARIANCE)),
    joyScore: BATH_NAR_JOY_SCORE,
    roiPct: BATH_NAR_ROI_PCT,
  };
}


// ─────────────────────────────────────────────────────────────────────
// 14. WINDOW REPLACEMENT COST CALCULATOR
// Sources: Modernize.com, This Old House 2026, HomeDepot.com
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const WINDOW_BASE_COST: Record<string, number> = {
  single_hung: 525, double_hung: 900, sliding: 600, picture: 450, bow_bay: 3000,
};
export const WINDOW_FRAME_MUL: Record<string, number> = {
  vinyl: 1.00, aluminum: 1.10, wood: 1.50, fiberglass: 1.60,
};
export const WINDOW_GLASS_MUL: Record<string, number> = {
  double: 1.00, triple: 1.30, argon: 1.15,
};
export const WINDOW_INSTALL_MUL: Record<string, number> = {
  retrofit: 1.00, full_frame: 1.35,
};
export const WINDOW_VARIANCE    = 0.20;
export const WINDOW_NAR_ROI_PCT = 74;

export type WindowType    = "single_hung" | "double_hung" | "sliding" | "picture" | "bow_bay";
export type WindowFrame   = "vinyl" | "aluminum" | "wood" | "fiberglass";
export type WindowGlass   = "double" | "triple" | "argon";
export type WindowInstall = "retrofit" | "full_frame";

export interface WindowInput {
  numWindows: number;
  windowType: WindowType;
  frameType: WindowFrame;
  glassType: WindowGlass;
  installType: WindowInstall;
}

export interface WindowResult {
  costPerWindow: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
  roiPct: number;
  estimatedValueAdded: number;
}

export function calcWindows(input: WindowInput): WindowResult {
  const costPerWindow = round(
    WINDOW_BASE_COST[input.windowType] *
    WINDOW_FRAME_MUL[input.frameType] *
    WINDOW_GLASS_MUL[input.glassType] *
    WINDOW_INSTALL_MUL[input.installType]
  );
  const totalMid = round(costPerWindow * input.numWindows);

  return {
    costPerWindow,
    totalMin: round(totalMid * (1 - WINDOW_VARIANCE)),
    totalMid,
    totalMax: round(totalMid * (1 + WINDOW_VARIANCE)),
    roiPct: WINDOW_NAR_ROI_PCT,
    estimatedValueAdded: round(totalMid * (WINDOW_NAR_ROI_PCT / 100)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 15. DECK COST CALCULATOR
// Sources: HomeGuide.com, Angi.com 2026, Forbes Home
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const DECK_COST_INSTALLED: Record<string, number> = {
  pressure_treated: 40, composite: 55, hardwood: 75,
};
export const DECK_COST_DIY: Record<string, number> = {
  pressure_treated: 4, composite: 18, hardwood: 32,
};
export const DECK_HEIGHT_MUL: Record<string, number> = {
  low: 1.00, medium: 1.15, high: 1.30,
};
export const DECK_STAIR_COST: Record<string, number> = {
  low: 1500, medium: 1800, high: 2500,
};
export const DECK_RAILING_PER_LF = 100;
export const DECK_VARIANCE       = 0.20;

export type DeckMaterial = "pressure_treated" | "composite" | "hardwood";
export type DeckHeight   = "low" | "medium" | "high";
export type DeckMode     = "installed" | "diy";

export interface DeckInput {
  lengthFt: number;
  widthFt: number;
  material: DeckMaterial;
  height: DeckHeight;
  mode: DeckMode;
  includeStairs: boolean;
  includeRailing: boolean;
}

export interface DeckResult {
  sqFt: number;
  costPerSqFt: number;
  baseCost: number;
  stairCost: number;
  railingCost: number;
  railingLf: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
}

export function calcDeck(input: DeckInput): DeckResult {
  const sqFt        = input.lengthFt * input.widthFt;
  const table       = input.mode === "diy" ? DECK_COST_DIY : DECK_COST_INSTALLED;
  const costPerSqFt = round(table[input.material] * DECK_HEIGHT_MUL[input.height], 2);
  const baseCost    = round(sqFt * costPerSqFt);
  const stairCost   = input.includeStairs  ? DECK_STAIR_COST[input.height] : 0;
  const railingLf   = input.includeRailing ? round(2 * input.lengthFt + input.widthFt) : 0;
  const railingCost = input.includeRailing ? round(railingLf * DECK_RAILING_PER_LF) : 0;
  const totalMid    = baseCost + stairCost + railingCost;

  return {
    sqFt: round(sqFt), costPerSqFt, baseCost, stairCost, railingCost, railingLf,
    totalMin: round(totalMid * (1 - DECK_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + DECK_VARIANCE)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 16. REMODELING ROI CALCULATOR
// Source: NAR/NARI Remodeling Impact Report 2025 — nar.realtor
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

// ⚠️  UPDATE ANNUALLY when NAR publishes the new Remodeling Impact Report
export interface RoiProject {
  id: string;
  name: string;
  avgCost: number;
  recoveryPct: number;
  joyScore: number;
  category: "exterior" | "interior" | "system";
}

export const ROI_PROJECTS: RoiProject[] = [
  { id: "steel_door",      name: "New Steel Front Door",               avgCost: 2300,   recoveryPct: 100, joyScore: 9.7, category: "exterior" },
  { id: "closet",          name: "Closet Renovation",                  avgCost: 4000,   recoveryPct: 83,  joyScore: 9.5, category: "interior" },
  { id: "fiberglass_door", name: "New Fiberglass Front Door",          avgCost: 4500,   recoveryPct: 80,  joyScore: 9.6, category: "exterior" },
  { id: "vinyl_windows",   name: "Vinyl Window Replacement",           avgCost: 17000,  recoveryPct: 74,  joyScore: 8.5, category: "exterior" },
  { id: "wood_windows",    name: "Wood Window Replacement",            avgCost: 21000,  recoveryPct: 71,  joyScore: 8.8, category: "exterior" },
  { id: "basement",        name: "Basement Conversion to Living Area", avgCost: 57500,  recoveryPct: 71,  joyScore: 9.5, category: "interior" },
  { id: "attic",           name: "Attic Conversion to Living Area",    avgCost: 91000,  recoveryPct: 67,  joyScore: 9.8, category: "interior" },
  { id: "kitchen_full",    name: "Complete Kitchen Renovation",        avgCost: 80000,  recoveryPct: 60,  joyScore: 10,  category: "interior" },
  { id: "kitchen_minor",   name: "Minor Kitchen Upgrade",              avgCost: 28000,  recoveryPct: 60,  joyScore: 9.8, category: "interior" },
  { id: "bath_add",        name: "Bathroom Addition",                  avgCost: 75000,  recoveryPct: 56,  joyScore: 9.5, category: "interior" },
  { id: "primary_suite",   name: "New Primary Suite Addition",         avgCost: 175000, recoveryPct: 54,  joyScore: 10,  category: "interior" },
  { id: "bath_reno",       name: "Bathroom Renovation",                avgCost: 35000,  recoveryPct: 50,  joyScore: 9.8, category: "interior" },
  { id: "roofing",         name: "Roof Replacement",                   avgCost: 22000,  recoveryPct: 60,  joyScore: 10,  category: "system"   },
  { id: "deck",            name: "New Deck / Patio",                   avgCost: 17000,  recoveryPct: 50,  joyScore: 9.3, category: "exterior" },
  { id: "hvac",            name: "HVAC System Replacement",            avgCost: 12000,  recoveryPct: 45,  joyScore: 9.0, category: "system"   },
  { id: "painting_ext",    name: "Exterior Painting",                  avgCost: 4800,   recoveryPct: 55,  joyScore: 9.0, category: "exterior" },
];

export interface RoiInput {
  projectId: string;
  projectCost: number;
  homeValue?: number;
}

export interface RoiResult {
  project: RoiProject;
  recoveryPct: number;
  valueRecovered: number;
  costNotRecovered: number;
  joyScore: number;
  homeValueWarning?: boolean;
  homeValuePct?: number;
}

export function calcROI(input: RoiInput): RoiResult {
  const project = ROI_PROJECTS.find((p) => p.id === input.projectId);
  if (!project) throw new Error(`Unknown project id: ${input.projectId}`);

  const valueRecovered   = round(input.projectCost * (project.recoveryPct / 100));
  const costNotRecovered = round(input.projectCost - valueRecovered);

  let homeValueWarning: boolean | undefined;
  let homeValuePct: number | undefined;
  if (input.homeValue && input.homeValue > 0) {
    homeValuePct     = round((input.projectCost / input.homeValue) * 100, 1);
    homeValueWarning = homeValuePct > 15;
  }

  return {
    project, recoveryPct: project.recoveryPct,
    valueRecovered, costNotRecovered,
    joyScore: project.joyScore,
    homeValueWarning, homeValuePct,
  };
}


// ─────────────────────────────────────────────────────────────────────
// 17. WATER HEATER REPLACEMENT COST CALCULATOR
// Sources: DOE Energy Saver, ENERGY STAR, IRS 25C, national contractor pricing surveys
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const WATER_HEATER_BASE_COST: Record<string, number> = {
  gas_tank: 2400,
  electric_tank: 1700,
  tankless_gas: 4300,
  heat_pump: 4100,
};
export const WATER_HEATER_CAPACITY_MUL: Record<string, number> = {
  small: 0.90, standard: 1.00, large: 1.18,
};
export const WATER_HEATER_URGENCY_COST: Record<string, number> = {
  normal: 0, urgent: 450, emergency: 900,
};
export const WATER_HEATER_MOD_COST: Record<string, number> = {
  none: 0, venting: 850, electrical: 1200, gas_line: 950,
};
export const WATER_HEATER_VARIANCE = 0.22;
export const WATER_HEATER_HPWH_CREDIT_MAX = 2000;
export const WATER_HEATER_HPWH_CREDIT_PCT = 0.30;

export type WaterHeaterType = "gas_tank" | "electric_tank" | "tankless_gas" | "heat_pump";
export type WaterHeaterCapacity = "small" | "standard" | "large";
export type WaterHeaterUrgency = "normal" | "urgent" | "emergency";
export type WaterHeaterModification = "none" | "venting" | "electrical" | "gas_line";

export interface WaterHeaterInput {
  heaterType: WaterHeaterType;
  capacity: WaterHeaterCapacity;
  urgency: WaterHeaterUrgency;
  modification: WaterHeaterModification;
  applyCredit: boolean;
}

export interface WaterHeaterResult {
  equipmentInstalledCost: number;
  urgencyCost: number;
  modificationCost: number;
  taxCredit: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
}

export function calcWaterHeater(input: WaterHeaterInput): WaterHeaterResult {
  const equipmentInstalledCost = round(
    WATER_HEATER_BASE_COST[input.heaterType] * WATER_HEATER_CAPACITY_MUL[input.capacity]
  );
  const urgencyCost = WATER_HEATER_URGENCY_COST[input.urgency];
  const modificationCost = WATER_HEATER_MOD_COST[input.modification];
  const grossTotal = equipmentInstalledCost + urgencyCost + modificationCost;
  const taxCredit = input.applyCredit && input.heaterType === "heat_pump"
    ? Math.min(round(grossTotal * WATER_HEATER_HPWH_CREDIT_PCT), WATER_HEATER_HPWH_CREDIT_MAX)
    : 0;
  const totalMid = grossTotal - taxCredit;

  return {
    equipmentInstalledCost,
    urgencyCost,
    modificationCost,
    taxCredit,
    totalMin: round(totalMid * (1 - WATER_HEATER_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + WATER_HEATER_VARIANCE)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 18. ROOF SHINGLE & BUNDLE CALCULATOR
// Sources: IRC roof assemblies, ARMA/NRCA bundle conventions
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const SHINGLE_PITCH_FACTOR: Record<string, number> = {
  low: 1.07, medium: 1.19, steep: 1.36,
};
export const SHINGLE_WASTE_FACTOR: Record<string, number> = {
  simple: 1.10, moderate: 1.15, complex: 1.20,
};
export const SHINGLE_BUNDLES_PER_SQUARE: Record<string, number> = {
  three_tab: 3,
  architectural: 3,
  premium: 4,
};
export const SHINGLE_UNDERLAYMENT_ROLL_SQFT = 400;
export const SHINGLE_STARTER_PER_BUNDLE_LF = 105;
export const SHINGLE_RIDGE_PER_BUNDLE_LF = 25;

export type ShingleType = "three_tab" | "architectural" | "premium";
export type ShinglePitch = "low" | "medium" | "steep";
export type ShingleComplexity = "simple" | "moderate" | "complex";

export interface RoofShingleInput {
  footprintSqFt: number;
  pitch: ShinglePitch;
  complexity: ShingleComplexity;
  shingleType: ShingleType;
  eaveRakeLf: number;
  ridgeHipLf: number;
}

export interface RoofShingleResult {
  roofSqFt: number;
  roofSquares: number;
  orderSquares: number;
  bundles: number;
  underlaymentRolls: number;
  starterBundles: number;
  ridgeCapBundles: number;
  wastePercent: number;
}

export function calcRoofShingles(input: RoofShingleInput): RoofShingleResult {
  const roofSqFt = round(input.footprintSqFt * SHINGLE_PITCH_FACTOR[input.pitch]);
  const roofSquares = round(roofSqFt / 100, 1);
  const wasteFactor = SHINGLE_WASTE_FACTOR[input.complexity];
  const orderSquares = round(roofSquares * wasteFactor, 1);

  return {
    roofSqFt,
    roofSquares,
    orderSquares,
    bundles: ceilInt(orderSquares * SHINGLE_BUNDLES_PER_SQUARE[input.shingleType]),
    underlaymentRolls: ceilInt((orderSquares * 100) / SHINGLE_UNDERLAYMENT_ROLL_SQFT),
    starterBundles: ceilInt(input.eaveRakeLf / SHINGLE_STARTER_PER_BUNDLE_LF),
    ridgeCapBundles: ceilInt(input.ridgeHipLf / SHINGLE_RIDGE_PER_BUNDLE_LF),
    wastePercent: Math.round((wasteFactor - 1) * 100),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 19. SIDING COST CALCULATOR
// Sources: DOE Building America wall drainage guidance, PEPA/VSI, national contractor pricing surveys
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const SIDING_COST_PER_SQFT: Record<string, number> = {
  vinyl: 8,
  fiber_cement: 14,
  engineered_wood: 12,
  stucco: 11,
  brick_veneer: 24,
};
export const SIDING_STORY_MUL: Record<string, number> = {
  one: 1.00, two: 1.12, three: 1.25,
};
export const SIDING_REMOVAL_COST_PER_SQFT = 1.6;
export const SIDING_WRAP_COST_PER_SQFT = 0.9;
export const SIDING_TRIM_OPENING_COST = 125;
export const SIDING_WASTE_FACTOR = 1.10;
export const SIDING_VARIANCE = 0.20;

export type SidingMaterial = "vinyl" | "fiber_cement" | "engineered_wood" | "stucco" | "brick_veneer";
export type SidingStories = "one" | "two" | "three";

export interface SidingInput {
  wallSqFt: number;
  material: SidingMaterial;
  stories: SidingStories;
  removeOld: boolean;
  houseWrap: boolean;
  openings: number;
}

export interface SidingResult {
  netSidingSqFt: number;
  materialInstallCost: number;
  removalCost: number;
  houseWrapCost: number;
  trimCost: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
}

export function calcSiding(input: SidingInput): SidingResult {
  const netSidingSqFt = round(input.wallSqFt * SIDING_WASTE_FACTOR);
  const materialInstallCost = round(
    netSidingSqFt * SIDING_COST_PER_SQFT[input.material] * SIDING_STORY_MUL[input.stories]
  );
  const removalCost = input.removeOld ? round(input.wallSqFt * SIDING_REMOVAL_COST_PER_SQFT) : 0;
  const houseWrapCost = input.houseWrap ? round(input.wallSqFt * SIDING_WRAP_COST_PER_SQFT) : 0;
  const trimCost = input.openings * SIDING_TRIM_OPENING_COST;
  const totalMid = materialInstallCost + removalCost + houseWrapCost + trimCost;

  return {
    netSidingSqFt,
    materialInstallCost,
    removalCost,
    houseWrapCost,
    trimCost,
    totalMin: round(totalMid * (1 - SIDING_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + SIDING_VARIANCE)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 20. DRIVEWAY / PAVING COST CALCULATOR
// Sources: FHWA gravel road guidance, PCA concrete flatwork guidance, national contractor pricing surveys
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const DRIVEWAY_COST_PER_SQFT: Record<string, number> = {
  gravel: 2.75,
  asphalt: 6.5,
  concrete: 10,
  pavers: 19,
};
export const DRIVEWAY_DEMOLITION_PER_SQFT = 2.25;
export const DRIVEWAY_BASE_COST_PER_SQFT = 1.4;
export const DRIVEWAY_THICKNESS_MUL: Record<string, number> = {
  light: 0.95, standard: 1.00, heavy: 1.18,
};
export const DRIVEWAY_VARIANCE = 0.22;

export type DrivewayMaterial = "gravel" | "asphalt" | "concrete" | "pavers";
export type DrivewayDuty = "light" | "standard" | "heavy";

export interface DrivewayInput {
  lengthFt: number;
  widthFt: number;
  material: DrivewayMaterial;
  duty: DrivewayDuty;
  removeExisting: boolean;
  newBase: boolean;
}

export interface DrivewayResult {
  areaSqFt: number;
  pavingCost: number;
  demolitionCost: number;
  baseCost: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
}

export function calcDriveway(input: DrivewayInput): DrivewayResult {
  const areaSqFt = round(input.lengthFt * input.widthFt);
  const pavingCost = round(areaSqFt * DRIVEWAY_COST_PER_SQFT[input.material] * DRIVEWAY_THICKNESS_MUL[input.duty]);
  const demolitionCost = input.removeExisting ? round(areaSqFt * DRIVEWAY_DEMOLITION_PER_SQFT) : 0;
  const baseCost = input.newBase ? round(areaSqFt * DRIVEWAY_BASE_COST_PER_SQFT) : 0;
  const totalMid = pavingCost + demolitionCost + baseCost;

  return {
    areaSqFt,
    pavingCost,
    demolitionCost,
    baseCost,
    totalMin: round(totalMid * (1 - DRIVEWAY_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + DRIVEWAY_VARIANCE)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 21. HOME ADDITION / ROOM ADDITION COST CALCULATOR
// Sources: IRC habitable room rules, IRS existing-home addition language, national contractor pricing surveys
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const HOME_ADDITION_COST_PER_SQFT: Record<string, number> = {
  bedroom: 210,
  bathroom: 360,
  kitchen: 430,
  family_room: 250,
  second_story: 330,
  garage: 115,
};
export const HOME_ADDITION_FINISH_MUL: Record<string, number> = {
  basic: 0.90, standard: 1.00, premium: 1.32,
};
export const HOME_ADDITION_FOUNDATION_PER_SQFT = 32;
export const HOME_ADDITION_DESIGN_PERMIT_PCT = 0.10;
export const HOME_ADDITION_VARIANCE = 0.25;

export type HomeAdditionType = "bedroom" | "bathroom" | "kitchen" | "family_room" | "second_story" | "garage";
export type HomeAdditionFinish = "basic" | "standard" | "premium";

export interface HomeAdditionInput {
  sqft: number;
  additionType: HomeAdditionType;
  finish: HomeAdditionFinish;
  foundation: boolean;
  designPermits: boolean;
  homeValue?: number;
}

export interface HomeAdditionResult {
  baseCost: number;
  foundationCost: number;
  designPermitCost: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
  homeValuePct?: number;
  budgetWarning?: boolean;
}

export function calcHomeAddition(input: HomeAdditionInput): HomeAdditionResult {
  const baseCost = round(
    input.sqft * HOME_ADDITION_COST_PER_SQFT[input.additionType] * HOME_ADDITION_FINISH_MUL[input.finish]
  );
  const foundationCost = input.foundation ? round(input.sqft * HOME_ADDITION_FOUNDATION_PER_SQFT) : 0;
  const designPermitCost = input.designPermits ? round((baseCost + foundationCost) * HOME_ADDITION_DESIGN_PERMIT_PCT) : 0;
  const totalMid = baseCost + foundationCost + designPermitCost;

  let homeValuePct: number | undefined;
  let budgetWarning: boolean | undefined;
  if (input.homeValue && input.homeValue > 0) {
    homeValuePct = round((totalMid / input.homeValue) * 100, 1);
    budgetWarning = homeValuePct > 30;
  }

  return {
    baseCost,
    foundationCost,
    designPermitCost,
    totalMin: round(totalMid * (1 - HOME_ADDITION_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + HOME_ADDITION_VARIANCE)),
    homeValuePct,
    budgetWarning,
  };
}


// ─────────────────────────────────────────────────────────────────────
// 22. POOL COST CALCULATOR
// Sources: DOE Energy Saver pool pump guidance, CPSC pool barrier safety, national installer pricing surveys
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const POOL_BASE_COST: Record<string, number> = {
  above_ground: 7500,
  vinyl_liner: 48000,
  fiberglass: 62000,
  concrete_gunite: 82000,
};
export const POOL_SIZE_MUL: Record<string, number> = {
  plunge: 0.78,
  standard: 1.00,
  large: 1.28,
};
export const POOL_SITE_COST: Record<string, number> = {
  easy: 0,
  moderate: 7500,
  difficult: 16000,
};
export const POOL_HEATER_COST: Record<string, number> = {
  none: 0,
  gas: 4800,
  heat_pump: 6200,
};
export const POOL_SALT_SYSTEM_COST = 2200;
export const POOL_BASIC_DECKING_COST = 9000;
export const POOL_VARIANCE = 0.24;

export type PoolType = "above_ground" | "vinyl_liner" | "fiberglass" | "concrete_gunite";
export type PoolSize = "plunge" | "standard" | "large";
export type PoolSite = "easy" | "moderate" | "difficult";
export type PoolHeater = "none" | "gas" | "heat_pump";

export interface PoolInput {
  poolType: PoolType;
  poolSize: PoolSize;
  site: PoolSite;
  heater: PoolHeater;
  saltSystem: boolean;
  basicDecking: boolean;
}

export interface PoolResult {
  poolShellInstalledCost: number;
  siteCost: number;
  heaterCost: number;
  saltSystemCost: number;
  deckingCost: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
}

export function calcPool(input: PoolInput): PoolResult {
  const poolShellInstalledCost = round(POOL_BASE_COST[input.poolType] * POOL_SIZE_MUL[input.poolSize]);
  const siteCost = POOL_SITE_COST[input.site];
  const heaterCost = POOL_HEATER_COST[input.heater];
  const saltSystemCost = input.saltSystem ? POOL_SALT_SYSTEM_COST : 0;
  const deckingCost = input.basicDecking ? POOL_BASIC_DECKING_COST : 0;
  const totalMid = poolShellInstalledCost + siteCost + heaterCost + saltSystemCost + deckingCost;

  return {
    poolShellInstalledCost,
    siteCost,
    heaterCost,
    saltSystemCost,
    deckingCost,
    totalMin: round(totalMid * (1 - POOL_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + POOL_VARIANCE)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 23. COUNTERTOP REPLACEMENT COST CALCULATOR
// Sources: NKBA kitchen budgeting guidance, NSF food-zone surface considerations, national fabricator pricing surveys
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const COUNTERTOP_COST_PER_SQFT: Record<string, number> = {
  laminate: 42,
  butcher_block: 58,
  solid_surface: 72,
  quartz: 95,
  granite: 88,
  marble: 135,
};
export const COUNTERTOP_EDGE_COST_PER_LF: Record<string, number> = {
  eased: 0,
  bevel: 9,
  ogee: 22,
  waterfall: 85,
};
export const COUNTERTOP_SINK_CUTOUT_COST = 350;
export const COUNTERTOP_COOKTOP_CUTOUT_COST = 450;
export const COUNTERTOP_DEMO_PER_SQFT = 8;
export const COUNTERTOP_BACKSPLASH_PER_LF = 38;
export const COUNTERTOP_VARIANCE = 0.18;

export type CountertopMaterial = "laminate" | "butcher_block" | "solid_surface" | "quartz" | "granite" | "marble";
export type CountertopEdge = "eased" | "bevel" | "ogee" | "waterfall";

export interface CountertopInput {
  sqft: number;
  edgeLf: number;
  material: CountertopMaterial;
  edge: CountertopEdge;
  sinkCutouts: number;
  cooktopCutouts: number;
  removeOld: boolean;
  backsplashLf: number;
}

export interface CountertopResult {
  surfaceCost: number;
  edgeCost: number;
  cutoutCost: number;
  demoCost: number;
  backsplashCost: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
}

export function calcCountertop(input: CountertopInput): CountertopResult {
  const surfaceCost = round(input.sqft * COUNTERTOP_COST_PER_SQFT[input.material]);
  const edgeCost = round(input.edgeLf * COUNTERTOP_EDGE_COST_PER_LF[input.edge]);
  const cutoutCost = input.sinkCutouts * COUNTERTOP_SINK_CUTOUT_COST + input.cooktopCutouts * COUNTERTOP_COOKTOP_CUTOUT_COST;
  const demoCost = input.removeOld ? round(input.sqft * COUNTERTOP_DEMO_PER_SQFT) : 0;
  const backsplashCost = round(input.backsplashLf * COUNTERTOP_BACKSPLASH_PER_LF);
  const totalMid = surfaceCost + edgeCost + cutoutCost + demoCost + backsplashCost;

  return {
    surfaceCost,
    edgeCost,
    cutoutCost,
    demoCost,
    backsplashCost,
    totalMin: round(totalMid * (1 - COUNTERTOP_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + COUNTERTOP_VARIANCE)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 24. GUTTER INSTALLATION COST CALCULATOR
// Sources: EPA stormwater guidance, SMACNA gutter/downspout practice, national installer pricing surveys
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const GUTTER_COST_PER_LF: Record<string, number> = {
  vinyl_sectional: 6,
  aluminum_seamless: 12,
  steel: 16,
  copper: 34,
};
export const GUTTER_STORY_MUL: Record<string, number> = {
  one: 1.00,
  two: 1.15,
  three: 1.32,
};
export const GUTTER_DOWNSPOUT_COST = 175;
export const GUTTER_GUARD_PER_LF = 9;
export const GUTTER_REMOVAL_PER_LF = 2.25;
export const GUTTER_FASCIA_REPAIR_PER_LF = 12;
export const GUTTER_VARIANCE = 0.20;

export type GutterMaterial = "vinyl_sectional" | "aluminum_seamless" | "steel" | "copper";
export type GutterStories = "one" | "two" | "three";

export interface GutterInput {
  linearFt: number;
  material: GutterMaterial;
  stories: GutterStories;
  downspouts: number;
  gutterGuards: boolean;
  removeOld: boolean;
  fasciaRepairLf: number;
}

export interface GutterResult {
  gutterCost: number;
  downspoutCost: number;
  guardCost: number;
  removalCost: number;
  fasciaRepairCost: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
}

export function calcGutters(input: GutterInput): GutterResult {
  const gutterCost = round(input.linearFt * GUTTER_COST_PER_LF[input.material] * GUTTER_STORY_MUL[input.stories]);
  const downspoutCost = input.downspouts * GUTTER_DOWNSPOUT_COST;
  const guardCost = input.gutterGuards ? round(input.linearFt * GUTTER_GUARD_PER_LF) : 0;
  const removalCost = input.removeOld ? round(input.linearFt * GUTTER_REMOVAL_PER_LF) : 0;
  const fasciaRepairCost = round(input.fasciaRepairLf * GUTTER_FASCIA_REPAIR_PER_LF);
  const totalMid = gutterCost + downspoutCost + guardCost + removalCost + fasciaRepairCost;

  return {
    gutterCost,
    downspoutCost,
    guardCost,
    removalCost,
    fasciaRepairCost,
    totalMin: round(totalMid * (1 - GUTTER_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + GUTTER_VARIANCE)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 25. BASEMENT FINISHING COST CALCULATOR
// Sources: IRC habitable basement/egress requirements, EPA moisture control guidance, NAR ROI data, national contractor pricing surveys
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const BASEMENT_FINISH_COST_PER_SQFT: Record<string, number> = {
  basic: 45,
  standard: 72,
  premium: 112,
};
export const BASEMENT_BATHROOM_COST: Record<string, number> = {
  none: 0,
  half: 14000,
  full: 24000,
};
export const BASEMENT_EGRESS_WINDOW_COST = 5200;
export const BASEMENT_MOISTURE_PREP_COST: Record<string, number> = {
  none: 0,
  minor: 3500,
  major: 9500,
};
export const BASEMENT_PERMIT_DESIGN_PCT = 0.08;
export const BASEMENT_VARIANCE = 0.24;
export const BASEMENT_NAR_ROI_PCT = 71;

export type BasementFinishLevel = "basic" | "standard" | "premium";
export type BasementBathroom = "none" | "half" | "full";
export type BasementMoisturePrep = "none" | "minor" | "major";

export interface BasementFinishingInput {
  sqft: number;
  finishLevel: BasementFinishLevel;
  bathroom: BasementBathroom;
  bedrooms: number;
  moisturePrep: BasementMoisturePrep;
  permitsDesign: boolean;
}

export interface BasementFinishingResult {
  finishCost: number;
  bathroomCost: number;
  egressCost: number;
  moisturePrepCost: number;
  permitDesignCost: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
  roiPct: number;
  estimatedValueAdded: number;
}

export function calcBasementFinishing(input: BasementFinishingInput): BasementFinishingResult {
  const finishCost = round(input.sqft * BASEMENT_FINISH_COST_PER_SQFT[input.finishLevel]);
  const bathroomCost = BASEMENT_BATHROOM_COST[input.bathroom];
  const egressCost = input.bedrooms * BASEMENT_EGRESS_WINDOW_COST;
  const moisturePrepCost = BASEMENT_MOISTURE_PREP_COST[input.moisturePrep];
  const subtotal = finishCost + bathroomCost + egressCost + moisturePrepCost;
  const permitDesignCost = input.permitsDesign ? round(subtotal * BASEMENT_PERMIT_DESIGN_PCT) : 0;
  const totalMid = subtotal + permitDesignCost;

  return {
    finishCost,
    bathroomCost,
    egressCost,
    moisturePrepCost,
    permitDesignCost,
    totalMin: round(totalMid * (1 - BASEMENT_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + BASEMENT_VARIANCE)),
    roiPct: BASEMENT_NAR_ROI_PCT,
    estimatedValueAdded: round(totalMid * (BASEMENT_NAR_ROI_PCT / 100)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 26. INSULATION CALCULATOR
// Sources: DOE/ENERGY STAR recommended R-values, IRS Section 25C sunset under Public Law 119-21, national installer pricing surveys
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const INSULATION_R_VALUE_PER_INCH: Record<string, number> = {
  fiberglass_batt: 3.2,
  blown_fiberglass: 2.7,
  cellulose: 3.5,
  open_cell_foam: 3.7,
  closed_cell_foam: 6.5,
};
export const INSULATION_COST_PER_SQFT_PER_R: Record<string, number> = {
  fiberglass_batt: 0.055,
  blown_fiberglass: 0.052,
  cellulose: 0.058,
  open_cell_foam: 0.135,
  closed_cell_foam: 0.22,
};
export const INSULATION_TARGET_R_BY_ZONE: Record<string, number> = {
  "1_2": 38,
  "3_4": 49,
  "5_7": 60,
  "8": 60,
};
export const INSULATION_AIR_SEALING_PER_SQFT = 0.55;
export const INSULATION_OLD_REMOVAL_PER_SQFT = 1.35;
export const INSULATION_WASTE_FACTOR = 1.10;
export const INSULATION_VARIANCE = 0.20;
export const INSULATION_25C_EXPIRED = true;

export type InsulationMaterial = "fiberglass_batt" | "blown_fiberglass" | "cellulose" | "open_cell_foam" | "closed_cell_foam";
export type InsulationClimateZone = "1_2" | "3_4" | "5_7" | "8";

export interface InsulationInput {
  areaSqFt: number;
  currentR: number;
  targetZone: InsulationClimateZone;
  material: InsulationMaterial;
  airSealing: boolean;
  removeOld: boolean;
}

export interface InsulationResult {
  targetR: number;
  addedR: number;
  requiredInches: number;
  orderAreaSqFt: number;
  insulationCost: number;
  airSealingCost: number;
  removalCost: number;
  taxCredit: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
}

export function calcInsulation(input: InsulationInput): InsulationResult {
  const targetR = INSULATION_TARGET_R_BY_ZONE[input.targetZone];
  const addedR = Math.max(0, targetR - input.currentR);
  const requiredInches = round(addedR / INSULATION_R_VALUE_PER_INCH[input.material], 1);
  const orderAreaSqFt = round(input.areaSqFt * INSULATION_WASTE_FACTOR);
  const insulationCost = round(orderAreaSqFt * addedR * INSULATION_COST_PER_SQFT_PER_R[input.material]);
  const airSealingCost = input.airSealing ? round(input.areaSqFt * INSULATION_AIR_SEALING_PER_SQFT) : 0;
  const removalCost = input.removeOld ? round(input.areaSqFt * INSULATION_OLD_REMOVAL_PER_SQFT) : 0;
  const taxCredit = 0;
  const totalMid = insulationCost + airSealingCost + removalCost - taxCredit;

  return {
    targetR,
    addedR: round(addedR),
    requiredInches,
    orderAreaSqFt,
    insulationCost,
    airSealingCost,
    removalCost,
    taxCredit,
    totalMin: round(totalMid * (1 - INSULATION_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + INSULATION_VARIANCE)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 27. CABINET REFACING / REPLACEMENT COST CALCULATOR
// Sources: NKBA planning guidance, national cabinet installer pricing surveys, FTC contractor guidance
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const CABINET_COST_PER_LF: Record<string, number> = {
  paint: 55,
  laminate_reface: 145,
  wood_veneer_reface: 275,
  stock_replace: 240,
  semi_custom_replace: 520,
  custom_replace: 850,
};
export const CABINET_DOOR_DRAWER_COST = 95;
export const CABINET_HARDWARE_COST = 18;
export const CABINET_DEMO_PER_LF = 35;
export const CABINET_LAYOUT_CHANGE_COST = 4200;
export const CABINET_COUNTERTOP_RISK_ALLOWANCE = 1800;
export const CABINET_VARIANCE = 0.22;

export type CabinetProjectType = "paint" | "laminate_reface" | "wood_veneer_reface" | "stock_replace" | "semi_custom_replace" | "custom_replace";

export interface CabinetInput {
  linearFt: number;
  projectType: CabinetProjectType;
  doorsDrawers: number;
  newHardware: boolean;
  removeOld: boolean;
  layoutChange: boolean;
  countertopRisk: boolean;
}

export interface CabinetResult {
  cabinetCost: number;
  doorDrawerCost: number;
  hardwareCost: number;
  demoCost: number;
  layoutCost: number;
  countertopRiskCost: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
}

export function calcCabinets(input: CabinetInput): CabinetResult {
  const cabinetCost = round(input.linearFt * CABINET_COST_PER_LF[input.projectType]);
  const isRefacing = input.projectType === "laminate_reface" || input.projectType === "wood_veneer_reface";
  const doorDrawerCost = isRefacing ? round(input.doorsDrawers * CABINET_DOOR_DRAWER_COST) : 0;
  const hardwareCost = input.newHardware ? round(input.doorsDrawers * CABINET_HARDWARE_COST) : 0;
  const demoCost = input.removeOld ? round(input.linearFt * CABINET_DEMO_PER_LF) : 0;
  const layoutCost = input.layoutChange ? CABINET_LAYOUT_CHANGE_COST : 0;
  const countertopRiskCost = input.countertopRisk ? CABINET_COUNTERTOP_RISK_ALLOWANCE : 0;
  const totalMid = cabinetCost + doorDrawerCost + hardwareCost + demoCost + layoutCost + countertopRiskCost;

  return {
    cabinetCost,
    doorDrawerCost,
    hardwareCost,
    demoCost,
    layoutCost,
    countertopRiskCost,
    totalMin: round(totalMid * (1 - CABINET_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + CABINET_VARIANCE)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 28. BACKUP GENERATOR SIZING & COST CALCULATOR
// Sources: FEMA/Ready.gov/CPSC generator safety guidance, NEC transfer equipment requirements, national installer pricing surveys
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const GENERATOR_LOAD_WATTS: Record<string, number> = {
  refrigerator: 800,
  sump_pump: 1000,
  well_pump: 1500,
  furnace_blower: 700,
  window_ac: 1200,
  central_ac_ton: 3500,
  lights_wifi: 600,
  microwave: 1200,
  medical: 500,
};
export const GENERATOR_STARTING_SURGE_FACTOR = 1.25;
export const GENERATOR_COST_PER_KW: Record<string, number> = {
  portable: 320,
  inverter: 520,
  standby: 1050,
};
export const GENERATOR_TRANSFER_COST: Record<string, number> = {
  none: 0,
  manual: 1200,
  automatic: 4200,
};
export const GENERATOR_FUEL_ALLOWANCE: Record<string, number> = {
  gasoline: 180,
  propane: 650,
  natural_gas: 1100,
};
export const GENERATOR_VARIANCE = 0.24;

export type GeneratorType = "portable" | "inverter" | "standby";
export type GeneratorTransfer = "none" | "manual" | "automatic";
export type GeneratorFuel = "gasoline" | "propane" | "natural_gas";

export interface GeneratorInput {
  refrigerator: boolean;
  sumpPump: boolean;
  wellPump: boolean;
  furnaceBlower: boolean;
  windowAc: number;
  centralAcTons: number;
  lightsWifi: boolean;
  microwave: boolean;
  medical: boolean;
  generatorType: GeneratorType;
  transfer: GeneratorTransfer;
  fuel: GeneratorFuel;
}

export interface GeneratorResult {
  runningWatts: number;
  surgeWatts: number;
  recommendedKw: number;
  equipmentCost: number;
  transferCost: number;
  fuelSetupCost: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
}

export function calcGenerator(input: GeneratorInput): GeneratorResult {
  const runningWatts =
    (input.refrigerator ? GENERATOR_LOAD_WATTS.refrigerator : 0) +
    (input.sumpPump ? GENERATOR_LOAD_WATTS.sump_pump : 0) +
    (input.wellPump ? GENERATOR_LOAD_WATTS.well_pump : 0) +
    (input.furnaceBlower ? GENERATOR_LOAD_WATTS.furnace_blower : 0) +
    input.windowAc * GENERATOR_LOAD_WATTS.window_ac +
    input.centralAcTons * GENERATOR_LOAD_WATTS.central_ac_ton +
    (input.lightsWifi ? GENERATOR_LOAD_WATTS.lights_wifi : 0) +
    (input.microwave ? GENERATOR_LOAD_WATTS.microwave : 0) +
    (input.medical ? GENERATOR_LOAD_WATTS.medical : 0);
  const surgeWatts = round(runningWatts * GENERATOR_STARTING_SURGE_FACTOR);
  const recommendedKw = Math.max(2, Math.ceil(surgeWatts / 1000));
  const equipmentCost = round(recommendedKw * GENERATOR_COST_PER_KW[input.generatorType]);
  const transferCost = GENERATOR_TRANSFER_COST[input.transfer];
  const fuelSetupCost = GENERATOR_FUEL_ALLOWANCE[input.fuel];
  const totalMid = equipmentCost + transferCost + fuelSetupCost;

  return {
    runningWatts,
    surgeWatts,
    recommendedKw,
    equipmentCost,
    transferCost,
    fuelSetupCost,
    totalMin: round(totalMid * (1 - GENERATOR_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + GENERATOR_VARIANCE)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 29. EV CHARGER INSTALLATION COST CALCULATOR
// Sources: DOE AFDC charging levels, IRS Form 8911, NEC Article 625, national electrician pricing surveys
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const EV_CHARGER_HARDWARE_COST: Record<string, number> = {
  outlet_only: 350,
  basic_32a: 650,
  smart_48a: 900,
  bidirectional_ready: 1800,
};
export const EV_CHARGER_INSTALL_BASE: Record<string, number> = {
  same_wall: 650,
  short_run: 1100,
  long_run: 2100,
  detached_garage: 3600,
};
export const EV_PANEL_UPGRADE_COST: Record<string, number> = {
  none: 0,
  load_management: 900,
  subpanel: 1800,
  service_upgrade: 4200,
};
export const EV_PERMIT_INSPECTION_COST = 250;
export const EV_30C_EXPIRED_AFTER = "2026-06-30";
export const EV_CHARGER_VARIANCE = 0.22;

export type EvChargerType = "outlet_only" | "basic_32a" | "smart_48a" | "bidirectional_ready";
export type EvRunLength = "same_wall" | "short_run" | "long_run" | "detached_garage";
export type EvPanelWork = "none" | "load_management" | "subpanel" | "service_upgrade";

export interface EvChargerInput {
  chargerType: EvChargerType;
  runLength: EvRunLength;
  panelWork: EvPanelWork;
  permit: boolean;
  trenching: boolean;
}

export interface EvChargerResult {
  hardwareCost: number;
  installCost: number;
  panelCost: number;
  permitCost: number;
  trenchingCost: number;
  federalCredit: number;
  totalMin: number;
  totalMid: number;
  totalMax: number;
}

export function calcEvCharger(input: EvChargerInput): EvChargerResult {
  const hardwareCost = EV_CHARGER_HARDWARE_COST[input.chargerType];
  const installCost = EV_CHARGER_INSTALL_BASE[input.runLength];
  const panelCost = EV_PANEL_UPGRADE_COST[input.panelWork];
  const permitCost = input.permit ? EV_PERMIT_INSPECTION_COST : 0;
  const trenchingCost = input.trenching ? 1200 : 0;
  const federalCredit = 0;
  const totalMid = hardwareCost + installCost + panelCost + permitCost + trenchingCost - federalCredit;

  return {
    hardwareCost,
    installCost,
    panelCost,
    permitCost,
    trenchingCost,
    federalCredit,
    totalMin: round(totalMid * (1 - EV_CHARGER_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + EV_CHARGER_VARIANCE)),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 30. SIDING MATERIAL CALCULATOR
// Sources: DOE Building America drainage plane guidance, Vinyl Siding Institute/PEPA installation guidance, manufacturer coverage conventions
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const SIDING_MATERIAL_COVERAGE_SQFT: Record<string, number> = {
  vinyl_carton: 200,
  fiber_cement_board: 7.25,
  engineered_wood_board: 8,
  cedar_shingle_square: 100,
};
export const SIDING_MATERIAL_WASTE: Record<string, number> = {
  simple: 1.10,
  moderate: 1.15,
  complex: 1.20,
};
export const SIDING_FASTENER_PER_SQFT = 2;
export const SIDING_HOUSE_WRAP_ROLL_SQFT = 900;
export const SIDING_TRIM_ALLOWANCE_PER_OPENING_LF = 14;

export type SidingMaterialCalcType = "vinyl_carton" | "fiber_cement_board" | "engineered_wood_board" | "cedar_shingle_square";
export type SidingMaterialComplexity = "simple" | "moderate" | "complex";

export interface SidingMaterialInput {
  wallSqFt: number;
  gablesSqFt: number;
  openingsSqFt: number;
  openings: number;
  material: SidingMaterialCalcType;
  complexity: SidingMaterialComplexity;
  includeHouseWrap: boolean;
}

export interface SidingMaterialResult {
  netWallSqFt: number;
  orderSqFt: number;
  unitsNeeded: number;
  fastenersNeeded: number;
  houseWrapRolls: number;
  trimLf: number;
  wastePercent: number;
}

export function calcSidingMaterial(input: SidingMaterialInput): SidingMaterialResult {
  const netWallSqFt = Math.max(0, input.wallSqFt + input.gablesSqFt - input.openingsSqFt);
  const factor = SIDING_MATERIAL_WASTE[input.complexity];
  const orderSqFt = round(netWallSqFt * factor);
  const unitsNeeded = ceilInt(orderSqFt / SIDING_MATERIAL_COVERAGE_SQFT[input.material]);
  const fastenersNeeded = ceilInt(orderSqFt * SIDING_FASTENER_PER_SQFT);
  const houseWrapRolls = input.includeHouseWrap ? ceilInt(orderSqFt / SIDING_HOUSE_WRAP_ROLL_SQFT) : 0;
  const trimLf = round(input.openings * SIDING_TRIM_ALLOWANCE_PER_OPENING_LF);

  return {
    netWallSqFt,
    orderSqFt,
    unitsNeeded,
    fastenersNeeded,
    houseWrapRolls,
    trimLf,
    wastePercent: Math.round((factor - 1) * 100),
  };
}


// ─────────────────────────────────────────────────────────────────────
// 31. RETAINING WALL CALCULATOR
// Sources: IRC/local permit thresholds, NCMA segmental retaining wall guidance, national hardscape pricing surveys
// Last verified: July 2026
// ─────────────────────────────────────────────────────────────────────

export const RETAINING_WALL_COST_PER_FACE_SQFT: Record<string, number> = {
  timber: 28,
  concrete_block: 45,
  poured_concrete: 72,
  natural_stone: 82,
  gabion: 48,
};
export const RETAINING_WALL_BLOCK_FACE_SQFT: Record<string, number> = {
  timber: 5.33,
  concrete_block: 1,
  poured_concrete: 1,
  natural_stone: 1,
  gabion: 9,
};
export const RETAINING_WALL_DRAINAGE_PER_LF = 18;
export const RETAINING_WALL_GEOGRID_PER_SQFT = 4.5;
export const RETAINING_WALL_EXCAVATION_PER_LF = 22;
export const RETAINING_WALL_ENGINEERING_COST = 1400;
export const RETAINING_WALL_VARIANCE = 0.25;

export type RetainingWallMaterial = "timber" | "concrete_block" | "poured_concrete" | "natural_stone" | "gabion";

export interface RetainingWallInput {
  lengthFt: number;
  exposedHeightFt: number;
  material: RetainingWallMaterial;
  drainage: boolean;
  geogrid: boolean;
  excavation: boolean;
  surcharge: boolean;
}

export interface RetainingWallResult {
  faceSqFt: number;
  unitsNeeded: number;
  wallCost: number;
  drainageCost: number;
  geogridCost: number;
  excavationCost: number;
  engineeringCost: number;
  permitReviewLikely: boolean;
  totalMin: number;
  totalMid: number;
  totalMax: number;
}

export function calcRetainingWall(input: RetainingWallInput): RetainingWallResult {
  const faceSqFt = round(input.lengthFt * input.exposedHeightFt);
  const unitsNeeded = ceilInt(faceSqFt / RETAINING_WALL_BLOCK_FACE_SQFT[input.material]);
  const wallCost = round(faceSqFt * RETAINING_WALL_COST_PER_FACE_SQFT[input.material]);
  const drainageCost = input.drainage ? round(input.lengthFt * RETAINING_WALL_DRAINAGE_PER_LF) : 0;
  const geogridCost = input.geogrid ? round(faceSqFt * RETAINING_WALL_GEOGRID_PER_SQFT) : 0;
  const excavationCost = input.excavation ? round(input.lengthFt * RETAINING_WALL_EXCAVATION_PER_LF) : 0;
  const permitReviewLikely = input.exposedHeightFt >= 4 || input.surcharge;
  const engineeringCost = permitReviewLikely ? RETAINING_WALL_ENGINEERING_COST : 0;
  const totalMid = wallCost + drainageCost + geogridCost + excavationCost + engineeringCost;

  return {
    faceSqFt,
    unitsNeeded,
    wallCost,
    drainageCost,
    geogridCost,
    excavationCost,
    engineeringCost,
    permitReviewLikely,
    totalMin: round(totalMid * (1 - RETAINING_WALL_VARIANCE)),
    totalMid: round(totalMid),
    totalMax: round(totalMid * (1 + RETAINING_WALL_VARIANCE)),
  };
}
